/**
 * Transport-unconfirmed message state (pure predicates).
 *
 * WHAT THIS IS
 *   When a Telnyx send hits an ambiguous transport failure — a Cloudflare
 *   504 "origin_gateway_timeout", any origin 5xx, or a client-side request
 *   timeout — the backend writes a PENDING status shape rather than an
 *   error shape:
 *
 *     { pending, pendingCode, expiredError, pendingSince }
 *
 *   It is deliberately keyed on `pending` and not `error`, because every
 *   existing reader of messages.status keys on `.error` and would otherwise
 *   render it as a red failure.
 *
 * WHY IT IS NOT AN ERROR
 *   A gateway timeout means the ORIGIN did not answer in time, not that the
 *   request failed. All 9 occurrences in the 30 days to 2026-08-10 were
 *   matched to orphan Telnyx delivery receipts and every one of them was
 *   DELIVERED. Showing those consultants a failure was simply wrong.
 *
 * WHY IT STILL EXPIRES
 *   We cannot leave a bubble pending forever. If reconciliation never claims
 *   the row (outside the provider's ~8-day detail-record window, cron down,
 *   or the match is too tight) the pending state ages out to the terminal
 *   string the backend supplied, and renders as a genuine failure from then
 *   on.
 *
 * Both strings come from the backend catalog so user-facing copy keeps one
 * home; this module only decides WHICH of the two applies.
 *
 * No imports, CommonJS export — same convention as telnyxPortPendingGates.js
 * so these rules can be unit-tested directly without module aliasing or a
 * React render.
 */

/**
 * How long a message may sit in the neutral pending state before it is
 * presented as a real failure.
 *
 * Six hours. Sized against what actually has to happen inside the window:
 * the reconciliation cron ticks every 15 minutes and carries a ~45 minute
 * safety margin, so a claim can legitimately take up to about an hour. Six
 * gives that several chances while still resolving inside the same working
 * day, so a consultant is never left indefinitely unsure whether a customer
 * heard from them.
 *
 * Note it is generous relative to the observed signal: on all 9 recorded
 * cases the provider's own delivery receipt arrived within about 5 minutes.
 */
const TRANSPORT_PENDING_WINDOW_HOURS = 6;

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * True when the message carries a transport-unconfirmed pending status.
 * Sending confirmation supersedes it: if a later delivery receipt (or
 * reconciliation) set sentAt, the normal sent/delivered rendering wins and
 * this returns false.
 */
const hasTransportPendingStatus = (message, statusObj) => {
  if (!statusObj || !statusObj.pending) {
    return false;
  }
  return !message || !message.sentAt;
};

/**
 * Resolve which of the two states applies right now.
 *
 * Returns { isPending, isExpired, text }:
 *   isPending — inside the window, render neutrally (grey, no error icon)
 *   isExpired — window elapsed with no confirmation, render as an error
 *   text      — the string to display for whichever state is active
 *
 * Falls back to the message's own createdAt when pendingSince is absent, so
 * a row written before pendingSince existed still ages out rather than
 * pending forever.
 */
const resolveTransportPendingState = (message, statusObj) => {
  if (!hasTransportPendingStatus(message, statusObj)) {
    return { isPending: false, isExpired: false, text: '' };
  }

  const since = statusObj.pendingSince
    || (message && (message.createdAt || message.scheduledAt));
  const startedAtMs = since ? new Date(since).getTime() : NaN;

  // Missing or unparseable timestamp: prefer the neutral state over wrongly
  // declaring a failure we cannot substantiate.
  if (!Number.isFinite(startedAtMs)) {
    return { isPending: true, isExpired: false, text: statusObj.pending };
  }

  const ageHours = (Date.now() - startedAtMs) / MS_PER_HOUR;

  if (ageHours >= TRANSPORT_PENDING_WINDOW_HOURS) {
    return {
      isPending: false,
      isExpired: true,
      text: statusObj.expiredError || statusObj.pending,
    };
  }

  return { isPending: true, isExpired: false, text: statusObj.pending };
};

module.exports = {
  TRANSPORT_PENDING_WINDOW_HOURS,
  hasTransportPendingStatus,
  resolveTransportPendingState,
};
